import {UIEvents, useInjection, visibilityEffect} from "@dp-instant/ui";
import {IEventEmitter, TypesCore} from "@dp-instant/interfaces";
import {Localize} from "@dp-instant/i18n";
import {apiClient} from "@game/api/ApiClient";
import {Images} from "@game/ui/images";
import {UIConstants} from "@game/ui/constants";
import {playUIClickSound} from "@game/helpers";
import {hapticMedium} from "@game/haptic";
import {PopupType} from "@game/ui/PopupType";
import cn from "classnames";
import {useState, useEffect} from "preact/hooks";
import {StarterOfferConfig, StarterOfferItem} from "../../../packages/shared/src/index";
import {SVG} from "../images";
import {GameModel} from "@game/coregame/model/GameModel";
import {TypesGame} from "@game/TypesGame";
import "./StarterOfferPopup.less";

type StarterOfferPopupProps = {
    config: StarterOfferConfig;
    onDone: () => void;
    initialStep?: "pack" | "upsell" | "downsell" | "done";
};

const LS_KEY_EXPIRES = "sio_starter_offer_expires";
const LS_KEY_CONFIG = "sio_starter_offer_config";

function fmtTimer(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

function getRemaining(): number {
    try {
        const exp = parseInt(localStorage.getItem(LS_KEY_EXPIRES) || "0");
        if (!exp) return 0;
        return Math.max(0, Math.floor((exp - Date.now()) / 1000));
    } catch { return 0; }
}

export function setOfferTimer(config: StarterOfferConfig) {
    try {
        const existing = parseInt(localStorage.getItem(LS_KEY_EXPIRES) || "0");
        if (!existing || existing <= Date.now()) {
            const minutes = config.timerMinutes ?? 30;
            localStorage.setItem(LS_KEY_EXPIRES, String(Date.now() + minutes * 60 * 1000));
        }
        localStorage.setItem(LS_KEY_CONFIG, JSON.stringify(config));
    } catch {}
    (window as any).__starterOfferConfig = config;
}

export function getOfferConfig(): StarterOfferConfig | null {
    if ((window as any).__starterOfferConfig) return (window as any).__starterOfferConfig;
    try {
        const raw = localStorage.getItem(LS_KEY_CONFIG);
        if (raw) {
            const config = JSON.parse(raw);
            (window as any).__starterOfferConfig = config;
            return config;
        }
    } catch {}
    return null;
}

export function clearOffer() {
    try {
        localStorage.removeItem(LS_KEY_EXPIRES);
        localStorage.removeItem(LS_KEY_CONFIG);
        localStorage.removeItem(LS_KEY_STEP);
    } catch {}
    (window as any).__starterOfferConfig = null;
}

const LS_KEY_STEP = "sio_starter_offer_step";

export function setOfferStep(step: "pack" | "upsell" | "downsell" | "done") {
    try { localStorage.setItem(LS_KEY_STEP, step); } catch {}
}

export function getOfferStep(): "pack" | "upsell" | "downsell" | "done" | null {
    try {
        const v = localStorage.getItem(LS_KEY_STEP);
        if (v === "pack" || v === "upsell" || v === "downsell" || v === "done") return v;
    } catch {}
    return null;
}

type OfferCardProps = {
    item: StarterOfferItem;
    title: string;
    subtitle: string;
    rows: Array<{value: string; label: string; icon?: "star" | "coin"}>;
    savingsText?: string;
    onBuyStars: () => void;
    onBuyUsd?: () => void;
    onSkip: () => void;
    onExpired: () => void;
    busy: boolean;
};

const OfferCard = ({item, title, subtitle, rows, savingsText, onBuyStars, onBuyUsd, onSkip, onExpired, busy}: OfferCardProps) => {
    const [invisible] = visibilityEffect(UIConstants.popup.startDelay);
    const [remaining, setRemaining] = useState(getRemaining);

    useEffect(() => {
        const id = setInterval(() => {
            const r = getRemaining();
            setRemaining(r);
            if (r <= 0) { clearInterval(id); onExpired(); }
        }, 1000);
        return () => clearInterval(id);
    }, []);

    const isRu = Localize.defaultLocale?.startsWith("ru");

    return (
        <div className="popups">
            <div className={cn("popup-starter-offer", {invisible})}>
                <div className="popup-starter-offer__badge">
                    {isRu ? "СПЕЦПРЕДЛОЖЕНИЕ" : "SPECIAL OFFER"}
                </div>
                <div className="popup-starter-offer__timer">
                    {fmtTimer(remaining)}
                </div>
                <div className="popup-starter-offer__title">{title}</div>
                <div className="popup-starter-offer__subtitle">{subtitle}</div>
                <div className="popup-starter-offer__items">
                    {rows.map((r, i) => (
                        <Fragment key={i}>
                            {i > 0 && <div className="popup-starter-offer__divider" />}
                            <div className="popup-starter-offer__item">
                                <div className="popup-starter-offer__item-value">
                                    {r.value} {r.icon === "star" && <Images.Star />}{r.icon === "coin" && <SVG.COINS className="offer-coin-icon" />}
                                </div>
                                <div className="popup-starter-offer__item-label">{r.label}</div>
                            </div>
                        </Fragment>
                    ))}
                </div>
                {savingsText && <div className="popup-starter-offer__savings">{savingsText}</div>}
                <div className="popup-starter-offer__buttons">
                    <button
                        className="popup-starter-offer__btn popup-starter-offer__btn--stars"
                        disabled={busy}
                        onClick={onBuyStars}
                    >
                        <Images.Star /> {item.price} Stars
                    </button>
                    {onBuyUsd && (
                        <button
                            className="popup-starter-offer__btn popup-starter-offer__btn--usd"
                            disabled={busy}
                            onClick={onBuyUsd}
                        >
                            {isRu ? "💳 Картой / СБП" : "💳 Card"}
                        </button>
                    )}
                </div>
                <button className="popup-starter-offer__skip" disabled={busy} onClick={onSkip}>
                    {isRu ? "Нет, спасибо" : "No, thanks"}
                </button>
            </div>
        </div>
    );
};

// Preact Fragment
const Fragment = ({children}: {children: any}) => children;

export const StarterOfferPopup = ({config, onDone, initialStep}: StarterOfferPopupProps) => {
    const dispatcher = useInjection<IEventEmitter>(TypesCore.dispatcher);
    const model = useInjection<GameModel>(TypesGame.model);
    const resumedStep = initialStep || getOfferStep() || "pack";
    const [step, setStepRaw] = useState<"pack" | "upsell" | "downsell" | "done">(resumedStep);
    const [boughtAny, setBoughtAny] = useState(false);
    const [busy, setBusy] = useState(false);
    const isRu = Localize.defaultLocale?.startsWith("ru");

    const setStep = (s: "pack" | "upsell" | "downsell" | "done") => {
        setStepRaw(s);
        setOfferStep(s);
    };

    const close = () => {
        // Persist current step so the 🎁 button resumes here, not from "pack"
        setOfferStep(step);
        dispatcher.emit(UIEvents.POPUP, {id: null});
        onDone();
    };

    const onExpired = () => {
        clearOffer();
        dispatcher.emit(UIEvents.POPUP, {id: null});
        onDone();
    };

    // Chain: pack → (skip→downsell | buy→upsell)
    //        downsell → (skip→done | buy→upsell)
    //        upsell → done
    const nextStep = (from: string, bought: boolean) => {
        if (bought) setBoughtAny(true);
        if (from === "pack" && bought) setStep("upsell");
        else if (from === "pack" && !bought) setStep("downsell");
        else if (from === "downsell" && bought) setStep("upsell");
        else if (from === "downsell" && !bought) close();
        else close();
    };

    const buyWithStars = async (slug: string): Promise<boolean> => {
        setBusy(true);
        try {
            const {url} = await apiClient.createInvoice(slug);
            const tg = (window as any).Telegram?.WebApp;
            if (!tg?.openInvoice || !url) { setBusy(false); return false; }
            return new Promise(resolve => {
                tg.openInvoice(url, async (status: string) => {
                    if (status === "paid") {
                        try {
                            const profile = await apiClient.getMe();
                            if (profile.coins !== undefined) model.cookie.coins = profile.coins;
                            if ((profile as any).premiumUntil) model.cookie.setVip = true;
                        } catch {}
                    }
                    setBusy(false);
                    resolve(status === "paid");
                });
            });
        } catch { setBusy(false); return false; }
    };

    const openPaymentMethod = (slug: string, price: number, offerTitle: string, tributePriceUsd?: number | null, from?: string) => {
        playUIClickSound();
        hapticMedium();
        const currentFrom = from || step;
        dispatcher.emit(UIEvents.POPUP, {
            id: PopupType.PAYMENT_METHOD,
            props: {
                slug, price, title: offerTitle, tributePriceUsd, hideBalance: true,
                onConfirm: (confirmed?: boolean) => {
                    let nextStepName: "pack" | "upsell" | "downsell" | "done" = step;
                    if (confirmed) {
                        if (currentFrom === "pack") nextStepName = "upsell";
                        else if (currentFrom === "downsell") nextStepName = "upsell";
                        else nextStepName = "done";
                        // If upsell purchased → activate VIP + clear offer
                        if (currentFrom === "upsell") {
                            model.cookie.setVip = true;
                            clearOffer();
                            dispatcher.emit(UIEvents.POPUP, {id: null});
                            onDone();
                            return;
                        }
                    } else {
                        if (currentFrom === "pack") nextStepName = "downsell";
                        else nextStepName = step;
                    }
                    dispatcher.emit(UIEvents.POPUP, {
                        id: PopupType.STARTER_OFFER,
                        props: {config, onDone, initialStep: nextStepName}
                    });
                }
            }
        });
    };

    if (step === "done" || !config.enabled) { close(); return null; }

    // PACK (49⭐: 2000 coins + 49 Stars)
    if (step === "pack" && config.pack) {
        const p = config.pack;
        apiClient.trackFunnelEvent("starter_offer_pack_shown");
        return (
            <OfferCard
                item={p}
                title={isRu ? "Стартовый набор" : "Starter Pack"}
                subtitle={isRu ? "Всё для быстрого старта!" : "Everything for a quick start!"}
                rows={[
                    {value: `${p.coins || 2000}`, label: isRu ? "монет" : "coins", icon: "coin"},
                    {value: `+${p.stars || 49}`, label: "Stars", icon: "star"},
                ]}
                savingsText={isRu ? "Экономия 60% от обычной цены" : "Save 60% vs regular price"}
                onBuyStars={async () => {
                    playUIClickSound(); hapticMedium();
                    apiClient.trackFunnelEvent("starter_offer_buy_clicked", {slug: p.slug, method: "stars"});
                    const ok = await buyWithStars(p.slug);
                    if (ok) apiClient.trackFunnelEvent("starter_offer_purchased", {slug: p.slug});
                    else apiClient.trackFunnelEvent("starter_offer_buy_cancelled", {slug: p.slug});
                    nextStep("pack", ok);
                }}
                onBuyUsd={() => { apiClient.trackFunnelEvent("starter_offer_buy_clicked", {slug: p.slug, method: "usd"}); openPaymentMethod(p.slug, p.price, isRu ? "Стартовый набор" : "Starter Pack", p.tributePriceUsd, "pack"); }}
                onSkip={() => { playUIClickSound(); apiClient.trackFunnelEvent("starter_offer_skipped", {step: "pack"}); nextStep("pack", false); }}
                onExpired={onExpired}
                busy={busy}
            />
        );
    }

    // DOWNSELL (29⭐: 1000 coins + 25 Stars)
    if (step === "downsell" && config.downsell) {
        const d = config.downsell;
        apiClient.trackFunnelEvent("starter_offer_downsell_shown");
        return (
            <OfferCard
                item={d}
                title={isRu ? "Мини-набор" : "Mini Pack"}
                subtitle={isRu ? "Специальная цена!" : "Special price!"}
                rows={[
                    {value: `${d.coins || 1000}`, label: isRu ? "монет" : "coins", icon: "coin"},
                    {value: `+${d.stars || 25}`, label: "Stars", icon: "star"},
                ]}
                onBuyStars={async () => {
                    playUIClickSound(); hapticMedium();
                    apiClient.trackFunnelEvent("starter_offer_buy_clicked", {slug: d.slug, method: "stars"});
                    const ok = await buyWithStars(d.slug);
                    if (ok) apiClient.trackFunnelEvent("starter_offer_purchased", {slug: d.slug});
                    else apiClient.trackFunnelEvent("starter_offer_buy_cancelled", {slug: d.slug});
                    nextStep("downsell", ok);
                }}
                onBuyUsd={() => { apiClient.trackFunnelEvent("starter_offer_buy_clicked", {slug: d.slug, method: "usd"}); openPaymentMethod(d.slug, d.price, isRu ? "Мини-набор" : "Mini Pack", d.tributePriceUsd, "downsell"); }}
                onSkip={() => { playUIClickSound(); apiClient.trackFunnelEvent("starter_offer_skipped", {step: "downsell"}); nextStep("downsell", false); }}
                onExpired={onExpired}
                busy={busy}
            />
        );
    }

    // UPSELL (199⭐: NoAds forever + 100 Stars)
    if (step === "upsell" && config.upsell) {
        const u = config.upsell;
        apiClient.trackFunnelEvent("starter_offer_upsell_shown");
        return (
            <OfferCard
                item={u}
                title={isRu ? "Премиум бандл" : "Premium Bundle"}
                subtitle={isRu ? "Одноразовое предложение!" : "One-time offer!"}
                rows={[
                    {value: isRu ? "Без рекламы" : "No Ads", label: isRu ? "навсегда" : "forever"},
                    {value: `+${u.stars || 100}`, label: "Stars", icon: "star"},
                ]}
                savingsText={isRu
                    ? `Отдельно: 300⭐. Здесь: ${u.price}⭐ + ${u.stars}⭐ бонус`
                    : `Separately: 300⭐. Here: ${u.price}⭐ + ${u.stars}⭐ bonus`}
                onBuyStars={async () => {
                    playUIClickSound(); hapticMedium();
                    apiClient.trackFunnelEvent("starter_offer_buy_clicked", {slug: u.slug, method: "stars"});
                    const ok = await buyWithStars(u.slug);
                    if (ok) {
                        apiClient.trackFunnelEvent("starter_offer_purchased", {slug: u.slug});
                        model.cookie.setVip = true;
                        clearOffer();
                    } else {
                        apiClient.trackFunnelEvent("starter_offer_buy_cancelled", {slug: u.slug});
                    }
                    close();
                }}
                onBuyUsd={() => { apiClient.trackFunnelEvent("starter_offer_buy_clicked", {slug: u.slug, method: "usd"}); openPaymentMethod(u.slug, u.price, isRu ? "Премиум бандл" : "Premium Bundle", u.tributePriceUsd, "upsell"); }}
                onSkip={() => { playUIClickSound(); close(); }}
                onExpired={onExpired}
                busy={busy}
            />
        );
    }

    close();
    return null;
};
